/**
 * Utility functions for nested accordion state management
 */

import type { FormField } from '@/types/formSchema';

/**
 * Get display label for an array item
 * Uses the item_identifier_field to find the value, or falls back to index
 * @param item - The array item data
 * @param field - Parent array field definition
 * @param index - Item index (for fallback)
 * @returns Display label for the item (e.g., "J1745", "99213")
 */
export function getArrayItemLabel(item: any, field: FormField, index: number): string {
  if (!item) return `Item ${index + 1}`;

  // Use item_identifier_field if specified
  if (field.item_identifier_field && item[field.item_identifier_field]) {
    return String(item[field.item_identifier_field]);
  }

  // Prefer the child the schema declares as this item's header
  // ((nomos.ui.v1.ui).is_header) over the name-based guess below.
  const headerFieldId = getArrayItemHeaderFieldId(item, field);
  if (headerFieldId) {
    return String(item[headerFieldId]);
  }

  // Common identifier fields to try
  const identifierFields = ['code', 'name', 'id', 'title', 'label'];
  for (const key of identifierFields) {
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      return String(item[key]);
    }
  }

  // Use item_label prefix if provided
  if (field.item_label) {
    return `${field.item_label} ${index + 1}`;
  }

  return `Item ${index + 1}`;
}

/**
 * Id of the child field whose value supplied this item's accordion title via
 * the schema's `isHeader` annotation — or `undefined` when the title came from
 * anywhere else (`item_identifier_field`, the name heuristic, or the index
 * fallback).
 *
 * Read-mode renderers use this to avoid printing the title again as a body row.
 * Because it reports the *resolution outcome* rather than merely "this field is
 * flagged", it is structurally impossible for that filter to hide a value with
 * nothing standing in for it: a blank header value returns `undefined`, so the
 * row stays visible.
 *
 * Evaluated per item, not per field definition — within one array, an item with
 * a populated header hides that row while a sibling with a blank one keeps it.
 *
 * @param item - The array item data
 * @param field - Parent array field definition
 */
export function getArrayItemHeaderFieldId(item: any, field: FormField): string | undefined {
  if (!item) return undefined;

  // item_identifier_field is resolved first, so it preempts the annotation.
  if (field.item_identifier_field && item[field.item_identifier_field]) return undefined;

  // First flagged child by schema order; a second one, if any, renders normally.
  const headerFieldId = field.fields?.find(subField => subField.isHeader)?.id;
  if (!headerFieldId || isEmptyValue(item[headerFieldId])) return undefined;

  return headerFieldId;
}

/**
 * The fields to render inside an array item's body.
 *
 * In read mode the header field is dropped when it supplied the item's title —
 * otherwise it shows twice, once as the accordion title and once as an ordinary
 * row. Edit mode returns every field, so identifiers stay correctable and the
 * value is never hidden from the person entering it.
 *
 * @param item - The array item data
 * @param field - Parent array field definition
 * @param isEditable - Whether the item is being edited
 */
export function getArrayItemBodyFields(
  item: any,
  field: FormField,
  isEditable?: boolean,
): FormField[] {
  const fields = field.fields || [];
  if (isEditable) return fields;

  const headerFieldId = getArrayItemHeaderFieldId(item, field);
  if (!headerFieldId) return fields;

  return fields.filter(subField => subField.id !== headerFieldId);
}

/**
 * Check if a value is considered "empty" (undefined, null, or empty string)
 */
function isEmptyValue(value: any): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Check if a field is a nested type (array or object)
 */
function isNestedFieldType(field: FormField): boolean {
  return field.type === 'array' || field.type === 'object';
}

/**
 * Check if an array item has all required fields filled
 * @param item - The array item data
 * @param field - Parent array field definition
 * @returns True if all required fields have values
 */
export function isArrayItemComplete(item: any, field: FormField): boolean {
  if (!item || !field.fields) return false;

  const scalarFields = field.fields.filter(f => !isNestedFieldType(f));
  const requiredFields = scalarFields.filter(f => f.validation?.required);

  return requiredFields.every(subField => !isEmptyValue(item[subField.id]));
}

/**
 * Check if an array field has valid data
 */
function isArrayFieldComplete(obj: any, subField: FormField): boolean {
  const items = obj[subField.id];
  const isRequired = subField.validation?.required;
  const hasItems = Array.isArray(items) && items.length > 0;

  return !isRequired || hasItems;
}

/**
 * Check if a scalar field has valid data
 */
function isScalarFieldComplete(obj: any, subField: FormField): boolean {
  const value = obj[subField.id];
  const isRequired = subField.validation?.required;

  return !isRequired || !isEmptyValue(value);
}

/**
 * Check if a nested object has all required fields filled
 * @param obj - The object data
 * @param field - Field definition with nested fields
 * @returns True if all required fields have values
 */
export function isObjectComplete(obj: any, field: FormField): boolean {
  if (!obj || !field.fields) return false;

  for (const subField of field.fields) {
    const isComplete = checkFieldCompletion(obj, subField);
    if (!isComplete) return false;
  }

  return true;
}

/**
 * Check completion status for a single field
 */
function checkFieldCompletion(obj: any, subField: FormField): boolean {
  if (subField.type === 'array') {
    return isArrayFieldComplete(obj, subField);
  }

  if (subField.type === 'object') {
    const nestedObj = obj[subField.id];
    const isRequired = subField.validation?.required;
    return !isRequired || isObjectComplete(nestedObj, subField);
  }

  return isScalarFieldComplete(obj, subField);
}

/**
 * Build a nested data path from base path and field id
 * @param basePath - The base path (e.g., "drug.0")
 * @param fieldId - The field id to append (e.g., "coverage")
 * @returns Full path (e.g., "drug.0.coverage")
 */
export function buildFieldPath(basePath: string, fieldId: string): string {
  if (!basePath) return fieldId;
  return `${basePath}.${fieldId}`;
}

/**
 * Get a nested value from an object using dot notation
 * @param obj - The object to get value from
 * @param path - Dot-notation path (e.g., "drug.0.coverage.copay")
 * @param defaultValue - Default value if not found
 * @returns The value at the path or default
 */
export function getNestedValue(obj: any, path: string, defaultValue: any = undefined): any {
  if (!obj || !path) return defaultValue;

  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value === undefined || value === null) {
      return defaultValue;
    }
    value = value[key];
  }

  return value !== undefined ? value : defaultValue;
}

/**
 * Clone (or create) the container `setNestedValue` is about to descend into.
 *
 * An empty array where the path names a property is a section wiped to `[]` (the
 * LA-7951 revert bug). Cloning it as an array would hang the next key on it as a named
 * property, which JSON drops, so every Edit → Save into the section silently vanished.
 * It becomes an object instead, so the edit lands and the section's shape is repaired.
 */
function cloneForDescent(existing: any, isNextKeyNumeric: boolean): any {
  if (existing === undefined || existing === null) return isNextKeyNumeric ? [] : {};
  if (Array.isArray(existing)) {
    return existing.length === 0 && !isNextKeyNumeric ? {} : [...existing];
  }
  return { ...existing };
}

/**
 * Set a nested value in an object immutably using dot notation
 * @param obj - The object to update
 * @param path - Dot-notation path (e.g., "drug.0.coverage.copay")
 * @param value - The value to set
 * @returns A new object with the updated value
 */
export function setNestedValue(obj: any, path: string, value: any): any {
  if (!path) return value;

  const keys = path.split('.');
  const result = Array.isArray(obj) ? [...obj] : { ...obj };

  let current: any = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const nextKey = keys[i + 1];

    // Determine if next level should be array or object
    const isNextKeyNumeric = /^\d+$/.test(nextKey);

    current[key] = cloneForDescent(current[key], isNextKeyNumeric);
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
  return result;
}

/**
 * Separate scalar fields from nested (array/object) fields
 * Fields marked `hidden` (stored and extracted, never rendered) are left out of both.
 * @param fields - Array of field definitions
 * @returns Object with scalar and nested fields separated
 */
export function separateFieldTypes(fields: FormField[]): {
  scalarFields: FormField[];
  nestedFields: FormField[];
} {
  const scalarFields: FormField[] = [];
  const nestedFields: FormField[] = [];

  for (const field of fields) {
    if (field.hidden) continue;
    if (field.type === 'array' || field.type === 'object') {
      nestedFields.push(field);
    } else {
      scalarFields.push(field);
    }
  }

  return { scalarFields, nestedFields };
}

/**
 * Count total items in all arrays within data for a set of fields
 * @param data - The data object
 * @param fields - Field definitions
 * @returns Total count of array items
 */
export function countArrayItems(data: any, fields: FormField[]): number {
  if (!data || !fields) return 0;

  let count = 0;
  for (const field of fields) {
    if (field.type === 'array') {
      const items = data[field.id];
      if (Array.isArray(items)) {
        count += items.length;
      }
    }
  }
  return count;
}

/**
 * Generate a unique key for expansion tracking
 * @param basePath - Base path in the data structure
 * @param fieldId - Field identifier
 * @param index - Optional index for array items
 * @returns Unique key string
 */
export function generateExpansionKey(basePath: string, fieldId: string, index?: number): string {
  let key = basePath ? `${basePath}.${fieldId}` : fieldId;
  if (index !== undefined) {
    key = `${key}.${index}`;
  }
  return key;
}

/**
 * Create an empty item based on field schema
 * Initializes nested objects and arrays as empty structures
 * Scalar fields are left undefined for user to fill in
 * @param field - The array field definition containing item schema
 * @returns An empty object matching the field's schema structure
 */
export function createEmptyItem(field: FormField): Record<string, any> {
  const item: Record<string, any> = {};

  if (!field.fields) return item;

  for (const subField of field.fields) {
    if (subField.type === 'array') {
      item[subField.id] = [];
    } else if (subField.type === 'object') {
      item[subField.id] = createEmptyItem(subField);
    }
    // Leave scalar fields undefined (user will fill them)
  }

  return item;
}
